import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSupabaseFunctionHeaders } from "@/lib/shopifySessionToken";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Trash2, UserPlus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

type PlatformRoleValue = "super_admin" | "relationship_manager" | "manager";

interface PlatformAdminRecord {
  id: string;
  user_id: string;
  email: string;
  name: string | null;
  role: PlatformRoleValue;
  created_at: string;
}

const ROLE_LABELS: Record<PlatformRoleValue, string> = {
  super_admin: "Super Admin",
  relationship_manager: "Relationship Manager",
  manager: "Manager",
};

export function PlatformUserManager({ canManage = false }: { canManage?: boolean }) {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<PlatformAdminRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    email: "",
    name: "",
    password: "",
    role: "manager" as PlatformRoleValue,
  });

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("platform_admins")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) throw error;
      setAdmins((data || []) as PlatformAdminRecord[]);
    } catch (error) {
      console.error("Failed to load platform admins", error);
      toast.error("Failed to load users and roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const handleRoleChange = async (adminId: string, role: PlatformRoleValue) => {
    try {
      const { error } = await supabase.from("platform_admins").update({ role }).eq("id", adminId);
      if (error) throw error;
      setAdmins((prev) => prev.map((admin) => (admin.id === adminId ? { ...admin, role } : admin)));
      toast.success("Role updated");
    } catch (error) {
      console.error("Failed to update role", error);
      toast.error("Failed to update role");
    }
  };

  const handleCreate = async () => {
    if (!newAdmin.email.trim() || !newAdmin.password.trim()) {
      toast.error("Email and password are required");
      return;
    }

    setSaving(true);
    try {
      const headers = await getSupabaseFunctionHeaders();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-platform-admin`,
        {
          method: "POST",
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: newAdmin.email.trim(),
            name: newAdmin.name.trim() || null,
            password: newAdmin.password,
            role: newAdmin.role,
          }),
        }
      );

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Failed to create platform user");
      }

      toast.success("Platform user added");
      setNewAdmin({
        email: "",
        name: "",
        password: "",
        role: "manager",
      });
      await loadAdmins();
    } catch (error: any) {
      console.error("Failed to add platform user", error);
      toast.error(error.message || "Failed to add platform user");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (admin: PlatformAdminRecord) => {
    if (admin.user_id === user?.id) {
      toast.error("You can't remove your own admin access here");
      return;
    }

    try {
      const { error } = await supabase.from("platform_admins").delete().eq("id", admin.id);
      if (error) throw error;
      setAdmins((prev) => prev.filter((item) => item.id !== admin.id));
      toast.success("Platform user removed");
    } catch (error) {
      console.error("Failed to remove platform user", error);
      toast.error("Failed to remove platform user");
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
      <Card>
        <CardHeader>
          <CardTitle>Users & Roles</CardTitle>
          <CardDescription>Manage platform admin access for Splitkart operations.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading users...
            </div>
          ) : admins.length === 0 ? (
            <p className="text-sm text-muted-foreground">No platform users found.</p>
          ) : (
            admins.map((admin) => (
              <div
                key={admin.id}
                className="flex flex-col gap-4 rounded-xl border p-4 md:flex-row md:items-center md:justify-between"
              >
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{admin.name || admin.email}</p>
                    {admin.user_id === user?.id && <Badge variant="secondary">You</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground">{admin.email}</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <Select
                    value={admin.role}
                    onValueChange={(value: PlatformRoleValue) => handleRoleChange(admin.id, value)}
                    disabled={!canManage}
                  >
                    <SelectTrigger className="w-[210px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="super_admin">{ROLE_LABELS.super_admin}</SelectItem>
                      <SelectItem value="relationship_manager">{ROLE_LABELS.relationship_manager}</SelectItem>
                      <SelectItem value="manager">{ROLE_LABELS.manager}</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(admin)}
                    disabled={!canManage || admin.user_id === user?.id}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Add Platform User</CardTitle>
          <CardDescription>
            Create a real admin login with email, password, and role.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="platform-user-email">Email</Label>
            <Input
              id="platform-user-email"
              value={newAdmin.email}
              disabled={!canManage}
              onChange={(event) => setNewAdmin((prev) => ({ ...prev, email: event.target.value }))}
              placeholder="admin@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="platform-user-name">Name</Label>
            <Input
              id="platform-user-name"
              value={newAdmin.name}
              disabled={!canManage}
              onChange={(event) => setNewAdmin((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="Admin Name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="platform-user-password">Password</Label>
            <Input
              id="platform-user-password"
              type="password"
              value={newAdmin.password}
              disabled={!canManage}
              onChange={(event) => setNewAdmin((prev) => ({ ...prev, password: event.target.value }))}
              placeholder="Minimum 8 characters"
            />
          </div>

          <div className="space-y-2">
            <Label>Role</Label>
            <Select
              value={newAdmin.role}
              disabled={!canManage}
              onValueChange={(value: PlatformRoleValue) => setNewAdmin((prev) => ({ ...prev, role: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="super_admin">{ROLE_LABELS.super_admin}</SelectItem>
                <SelectItem value="relationship_manager">{ROLE_LABELS.relationship_manager}</SelectItem>
                <SelectItem value="manager">{ROLE_LABELS.manager}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={handleCreate} disabled={saving || !canManage} className="w-full">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
            Add User
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
